/**
 * views/VendorTaxSettingsView.js
 * ----------------------------------------------------------------------------
 * Every vendor on the platform, across all vendor types, with their tax
 * position in one row: VAT registration, EFRIS identity (TIN, device,
 * enrolment, environment) and who files with URA. The row opens the ONE EFRIS
 * identity editor (components/fiscal/EfrisIdentityEditor.js).
 *
 * WHY THIS PAGE EXISTS. The EFRIS identity used to be edited from a button on
 * the wallet dashboard's commission table, because that table happened to
 * list every vendor. Nobody looking for "which vendors can file to URA live"
 * would think to open Disbursements. During the URA UAT the question is asked
 * directly, so it gets a direct answer under Tax, filterable by vendor type
 * and by the vendor's next step. The EFRIS column reads the same helper as the
 * wallet dashboard's (utilities/efrisState.js), so both pages always agree on
 * who is live.
 *
 * WHAT IS NOT HERE, AND WHY. The vendor's own choice of who files
 * (components/VendorTaxSettings.js) is self-scoped to the signed-in vendor by
 * design: routes/vendorTaxRoutes.js has no :vendorId form so that no vendor
 * can read another's tax number by guessing an id. That choice is therefore
 * shown here as a read-only column from the contract row, not edited, and the
 * copy says where the vendor changes it. Whether Lusuku fiscalises for a
 * vendor at all is the EFRIS_FISCALISATION entitlement and lives on the
 * Feature entitlements page.
 *
 * The data is GET /api/vendor-commissions, the same rows the wallet reads, so
 * there is one source for a vendor's TIN rather than two that can disagree.
 *
 * WHERE ENROLMENT HAPPENS (23 Oct 2026). The row's action used to sit in a
 * ninth column past the right edge of the card, so the page read as read-only
 * and "where do we enrol from" had no visible answer. The table now fits:
 * device number and ops note ride under the EFRIS state rather than in columns
 * of their own, the action names the vendor's next step ("Record
 * application", "Add credentials", "Go live", from the server's `efris.stage`),
 * and it is pinned to the right edge so it never scrolls out of view.
 *
 * Above the table, "How a vendor gets onto EFRIS" states the sequence, and
 * every step is a control: step 1 (the vendor enrols with URA themselves, as
 * every entity carries its own tax obligation) opens URA's portal; steps 2 to
 * 4 show the vendors waiting on that step, whose row buttons are that step.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { apiGetVendorCommissionContracts } from "../api/index.js";
import { usePermissions } from "../utilities/usePermissions";
import { describeEfrisState } from "../utilities/efrisState.js";
import { AppButton, AppLinkButton, AppSelect } from "../components/ui";
import Loading from "../components/Loading";
import FiscalModeBadge from "../components/fiscal/FiscalModeBadge.js";
import EfrisIdentityEditor from "../components/fiscal/EfrisIdentityEditor.js";
import { DeniedNotice, PAGE, Pill, TYPE, errorText, fmtVendorType } from "../components/fiscal/fiscalUi.js";
import TaxHelp from "../components/fiscal/taxHelp.js";

const VENDOR_TYPES = ["RESTAURANT", "RETAILER", "WHOLESALER", "HEALTHCARE", "BRAND"];

/** The wallet's badge tones, mapped onto this page's pill tones. */
const EFRIS_TONE = { green: "good", purple: "purple", neutral: "neutral" };

const ENROLMENT_LABEL = {
  NOT_ENROLLED: "Not enrolled",
  PENDING: "Pending",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
};

/** URA's production portal, where a vendor enrols. */
const URA_EFRIS_PORTAL = "https://efris.ura.go.ug";

/**
 * Each vendor's next step with URA, as the server decides it (`efris.stage`,
 * models/shared/efrisFields.js efrisEnrolmentStage). The page only words it:
 * the step strip, the filter, the count line and each row's button all read
 * this one table, so a vendor is never "ready to go live" in one place and
 * "waiting on credentials" in another.
 */
const STAGES = Object.freeze({
  RECORD_APPLICATION: { filter: "Record the application", action: "Record application", icon: "fa fa-plus", count: "to record" },
  ADD_CREDENTIALS: { filter: "Add the credentials", action: "Add credentials", icon: "fa fa-key", count: "waiting on credentials" },
  GO_LIVE: { filter: "Go live", action: "Go live", icon: "fa fa-play", count: "ready to go live" },
  LIVE: { filter: "Filing live", action: "Update EFRIS", icon: "fa fa-pen", count: "filing live" },
  SUSPENDED: { filter: "Suspended", action: "Update EFRIS", icon: "fa fa-pen", count: "suspended" },
});
const STAGE_ORDER = Object.keys(STAGES);
const stageOf = (contract) => (STAGES[contract?.efris?.stage] ? contract.efris.stage : "RECORD_APPLICATION");

/**
 * The sequence, in the order it happens. Step 1 is the vendor's own, at URA,
 * so its control opens URA's portal. Steps 2 to 4 happen on this page, so each
 * one's control shows the vendors waiting on it, and each of those rows'
 * button is that step.
 */
const ENROLMENT_STEPS = [
  {
    title: "The vendor enrols with URA",
    body: "Each vendor registers for EFRIS on URA's portal under their own TIN. Lusuku does not enrol for them.",
    portal: URA_EFRIS_PORTAL,
  },
  {
    title: "Record the application",
    body: "Enter the TIN, set enrolment to Pending and put URA's reference in the ops note.",
    stage: "RECORD_APPLICATION",
  },
  {
    title: "Add the credentials",
    body: "When URA issues them, enter the device number (DSN) and set enrolment to Active. The vendor stays on Test.",
    stage: "ADD_CREDENTIALS",
  },
  {
    title: "Go live",
    body: "Once URA confirms the device works, switch the environment to Live. The vendor chooses who files on their own profile.",
    stage: "GO_LIVE",
  },
];

/* Column names, said once: the header row on a wide screen and each card's
   field labels on a phone (lk-table--stack). */
const COLUMN = {
  vat: "VAT",
  efris: "EFRIS enrolment",
  environment: "Environment",
  whoFiles: "Who files",
};

const S = {
  steps: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 10,
    margin: "0 0 16px",
    padding: 0,
    listStyle: "none",
  },
  step: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  stepNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: 999,
    background: "#083860",
    color: "#fff",
    ...TYPE.caption,
    fontWeight: 700,
    marginRight: 8,
  },
  stepTitle: { ...TYPE.body, fontWeight: 700, color: "#0f172a" },
  stepBody: { ...PAGE.note, margin: 0, flex: "1 1 auto" },
  index: { width: 44, color: "#8595a8", fontVariantNumeric: "tabular-nums" },
  note: {
    display: "block",
    maxWidth: 260,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontStyle: "italic",
  },
};

/* Hints match taxHelp "whoFiles": Lusuku files only its own documents today,
   so neither choice makes Lusuku file a vendor's sales. */
const REMITTANCE = {
  VENDOR_SELF: { label: "Vendor files", tone: "neutral", hint: "The vendor files its own sales with URA under its own TIN. The default." },
  LUSUKU_ON_BEHALF: { label: "Lusuku files", tone: "info", hint: "The vendor has authorised Lusuku to submit under its TIN. Recorded only: Lusuku files no document under a vendor's TIN today." },
};

const VendorTaxSettingsView = () => {
  const { can, loaded: permissionsLoaded } = usePermissions();
  const canManage = permissionsLoaded && can("tax:manage");

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiGetVendorCommissionContracts();
      setContracts(Array.isArray(data?.contracts) ? data.contracts : []);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
      else setError(errorText(err, "Could not load vendors."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!denied) load();
  }, [load, denied]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contracts.filter((c) => {
      if (typeFilter && c.vendorType !== typeFilter) return false;
      if (stageFilter && stageOf(c) !== stageFilter) return false;
      if (q && ![c.name, c.vendorType, c.vatRegistrationNumber, c.efris?.tin, c.efris?.deviceNumber]
        .some((v) => String(v || "").toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  }, [contracts, typeFilter, stageFilter, query]);

  const counts = useMemo(() => {
    const out = Object.fromEntries(STAGE_ORDER.map((k) => [k, 0]));
    contracts.forEach((c) => { out[stageOf(c)] += 1; });
    return out;
  }, [contracts]);

  const filtersActive = Boolean(typeFilter || stageFilter || query);

  /* A step's control: show the vendors waiting on it, and bring the table into
     view, since the strip sits above it. */
  const showStage = (stage) => {
    setStageFilter(stage);
    const table = typeof document !== "undefined" ? document.getElementById("vendor-tax-table") : null;
    if (table && table.scrollIntoView) table.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (denied) {
    return <DeniedNotice what="A vendor's EFRIS identity decides which taxpayer Lusuku files under." />;
  }

  return (
    <div style={PAGE.page}>
      <div style={PAGE.card}>
        <div style={PAGE.cardHeader}>
          <div>
            <h4 style={PAGE.cardTitle}>Vendor tax settings<TaxHelp topic="efrisEnrolment" /></h4>
            <p style={PAGE.cardSubtitle}>
              Each vendor's taxpayer identity for EFRIS, where they have got to with URA enrolment, and whether
              documents for them would go to URA's sandbox or to production. VAT registration is shown here but
              set on the vendor's contract under Wallet, Commission. A vendor chooses who files on their own
              profile, under Tax and receipts.
            </p>
          </div>
          <AppButton variant="outline" icon="fa fa-sync" onClick={load} disabled={loading}>Refresh</AppButton>
        </div>

        <div style={PAGE.cardBody}>
          <p style={PAGE.sectionTitle}>How a vendor gets onto EFRIS</p>
          <ol style={S.steps} aria-label="How a vendor gets onto EFRIS">
            {ENROLMENT_STEPS.map((step, i) => (
              <li key={step.title} style={S.step} data-testid="enrolment-step">
                <div>
                  <span style={S.stepNumber} aria-hidden="true">{i + 1}</span>
                  <span style={S.stepTitle}>{step.title}</span>
                </div>
                <p style={S.stepBody}>{step.body}</p>
                <div>
                  {step.portal ? (
                    <AppLinkButton size="sm" href={step.portal}>Open URA's EFRIS portal</AppLinkButton>
                  ) : (
                    <AppButton
                      size="sm"
                      icon="fa fa-filter"
                      disabled={!counts[step.stage]}
                      onClick={() => showStage(step.stage)}
                    >
                      {counts[step.stage]
                        ? `Show ${counts[step.stage]} vendor${counts[step.stage] === 1 ? "" : "s"}`
                        : "No vendors here"}
                    </AppButton>
                  )}
                </div>
              </li>
            ))}
          </ol>

          <div style={PAGE.toolbar}>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 170 }}>
              <label className="lk-field__label" htmlFor="vts-type">Vendor type</label>
              <AppSelect id="vts-type" block value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All vendor types</option>
                {VENDOR_TYPES.map((t) => <option key={t} value={t}>{fmtVendorType(t)}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 210 }}>
              <label className="lk-field__label" htmlFor="vts-stage">Next step<TaxHelp topic="nextStep" /></label>
              <AppSelect id="vts-stage" block value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                <option value="">Any step</option>
                {STAGE_ORDER.map((key) => (
                  <option key={key} value={key}>{`${STAGES[key].filter} (${counts[key]})`}</option>
                ))}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, flex: "1 1 220px" }}>
              <label className="lk-field__label" htmlFor="vts-q">Search</label>
              <input
                id="vts-q"
                className="lk-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Vendor name, TIN or device number"
              />
            </div>
            {filtersActive ? (
              <AppButton variant="quiet" onClick={() => { setTypeFilter(""); setStageFilter(""); setQuery(""); }}>
                Clear filters
              </AppButton>
            ) : null}
          </div>

          {loading ? <Loading size="panel" label="Loading vendors..." /> : null}

          {!loading && error ? (
            <div className="lk-note lk-note--danger" style={{ marginBottom: 14 }}>
              <span>{error}</span>
              <AppButton size="sm" variant="quiet" onClick={load} style={{ marginLeft: "auto" }}>Retry</AppButton>
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <p style={PAGE.count}>
                {filtersActive ? `${visible.length} of ${contracts.length} vendors` : `${contracts.length} vendors`}
                {" "}
                <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
                  ({STAGE_ORDER.filter((k) => k !== "SUSPENDED" || counts[k])
                    .map((k) => `${counts[k]} ${STAGES[k].count}`)
                    .join(", ")})
                </span>
              </p>
              <div className="lk-table-frame" id="vendor-tax-table">
                <Table responsive hover className="lk-table lk-table--stack">
                  <thead>
                    <tr>
                      <th style={S.index}>#</th>
                      <th>Vendor</th>
                      <th>{COLUMN.vat}<TaxHelp topic="vatCharging" /></th>
                      <th>{COLUMN.efris}<TaxHelp topic="efrisEnrolment" /></th>
                      <th>{COLUMN.environment}<TaxHelp topic="vendorEnvironment" /></th>
                      <th>{COLUMN.whoFiles}<TaxHelp topic="whoFiles" /></th>
                      <th className="lk-table-sticky-end" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={PAGE.empty}>
                          {contracts.length === 0 ? "No vendors yet." : "No vendors match these filters."}
                        </td>
                      </tr>
                    ) : null}
                    {visible.map((contract, index) => {
                      const efris = contract.efris || {};
                      const described = describeEfrisState(contract);
                      const remittance = REMITTANCE[efris.remittance] || REMITTANCE.VENDOR_SELF;
                      const enrolment =
                        ENROLMENT_LABEL[efris.enrolmentStatus] || efris.enrolmentStatus || ENROLMENT_LABEL.NOT_ENROLLED;
                      return (
                        <tr key={`${contract.vendorType}-${contract.contractId}`} data-testid="vendor-row">
                          <td className="lk-table-stack-hide" style={S.index}>{index + 1}</td>
                          <td className="lk-table-stack-title">
                            <span style={PAGE.strong}>{contract.name}</span>
                            <span className="lk-table-meta">
                              {[fmtVendorType(contract.vendorType), contract.country].filter(Boolean).join(", ")}
                            </span>
                          </td>
                          <td data-label={COLUMN.vat}>
                            {contract.vatRegistered === false ? (
                              <Pill tone="warn">NOT CHARGING</Pill>
                            ) : (
                              <>
                                {contract.chargesVat
                                  ? <Pill tone="good">CHARGING</Pill>
                                  : <Pill tone="warn">NO TIN, NOT CHARGING</Pill>}
                                <span className="lk-table-meta" style={contract.vatRegistrationNumber ? undefined : { color: "#b42318", fontWeight: 600 }}>
                                  {contract.vatRegistrationNumber ? `TIN ${contract.vatRegistrationNumber}` : "No TIN on file"}
                                </span>
                              </>
                            )}
                          </td>
                          <td data-testid="efris-cell" data-label={COLUMN.efris}>
                            <Pill tone={EFRIS_TONE[described.tone] || "neutral"}>{described.label}</Pill>
                            <span className="lk-table-meta">Enrolment: {enrolment}</span>
                            {described.meta ? <span className="lk-table-meta">{described.meta}</span> : null}
                            {efris.deviceNumber ? (
                              <span className="lk-table-meta">
                                Device <span style={PAGE.mono}>{efris.deviceNumber}</span>
                              </span>
                            ) : null}
                            {efris.note ? (
                              <span className="lk-table-meta" style={S.note} title={efris.note}>{efris.note}</span>
                            ) : null}
                          </td>
                          <td data-label={COLUMN.environment}><FiscalModeBadge mode={efris.mode || "TEST"} /></td>
                          <td data-label={COLUMN.whoFiles}>
                            <Pill tone={remittance.tone} title={remittance.hint}>{remittance.label}</Pill>
                          </td>
                          <td className="lk-table-sticky-end" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            {canManage ? (
                              <AppButton
                                size="sm"
                                icon={STAGES[stageOf(contract)].icon}
                                onClick={() => setEditing(contract)}
                              >
                                {STAGES[stageOf(contract)].action}
                              </AppButton>
                            ) : (
                              <span style={PAGE.muted}>Read only</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <EfrisIdentityEditor
        contract={editing}
        onClose={() => setEditing(null)}
        onSaved={load}
        onReload={load}
      />
    </div>
  );
};

export default VendorTaxSettingsView;
